import { useState } from "react";
import { Search, Bell, Moon, Sun, HelpCircle, Command, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { MobileNav } from "./MobileNav";
import { IndicadoresBadge } from "./IndicadoresBadge";

import { setAppearance, useAppearance } from "@/lib/appearance";
import { syncService } from "@/services/sync.service";
import { cn } from "@/lib/utils";

export function TopBar({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { dark } = useAppearance();
  const [syncing, setSyncing] = useState(false);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await syncService.runManual();
      if (r.estado === "error") {
        toast.error(r.mensaje ?? "No fue posible sincronizar con Mercado Público");
      } else if (r.estado === "parcial") {
        toast.info(r.mensaje ?? "Sincronización parcial con Mercado Público");
      } else {
        toast.success(
          `Sincronizado con Mercado Público: ${r.nuevas} nuevas, ${r.cambiosDetectados} cambios, ${r.actualizadas} sin cambios`,
        );
      }
    } catch {
      toast.error("No fue posible sincronizar con Mercado Público");
    } finally {
      setSyncing(false);
    }
  };


  return (
    <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="flex items-center gap-2 md:gap-4 px-3 md:px-5 lg:px-8 h-16">
        <MobileNav />

        <div className="flex-1 min-w-0">
          <h1 className="font-display text-base md:text-lg lg:text-xl font-semibold text-foreground truncate">
            {title}
          </h1>
          {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
        </div>

        <div className="hidden lg:flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 w-72">
          <Search className="size-3.5 text-muted-foreground" />
          <input
            className="bg-transparent text-sm outline-none flex-1 placeholder:text-muted-foreground"
            placeholder="Buscar licitaciones, documentos..."
          />
          <kbd className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
            <Command className="size-2.5" />K
          </kbd>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <IndicadoresBadge />
          {actions}

          <button
            onClick={() => setAppearance({ dark: !dark })}
            className="size-9 grid place-items-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
            title="Cambiar tema"
            aria-label="Cambiar tema"
          >
            {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <button
            className="hidden sm:grid size-9 place-items-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
            title="Ayuda"
            aria-label="Ayuda"
          >
            <HelpCircle className="size-4" />
          </button>
          <button
            onClick={runSync}
            disabled={syncing}
            className="size-9 grid place-items-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition disabled:opacity-50"
            title="Sincronizar con Mercado Público"
            aria-label="Sincronizar con Mercado Público"
          >
            <RefreshCw className={cn("size-4", syncing && "animate-spin")} />
          </button>
          <button
            className="size-9 grid place-items-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition relative"
            title="Notificaciones"
            aria-label="Notificaciones"
          >
            <Bell className="size-4" />
            <span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-primary" />
          </button>
        </div>
      </div>
    </header>
  );
}
